import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from '@usas/ui-web/wrappers/ReactRouterDom';

import AthleteUserConstants from '../AthleteUserConstants';
import NavLinks from '../../../meet/NavLinks';
import EntryNavLinks from '../../EntryNavLinks';
import MyAthleteEntryNavLinks from '../../../myAthleteEntry/MyAthleteEntryNavLinks';
import MyMeetsNavLinks from '../../../myMeets/MyMeetsNavLinks';

import useOmeMeetData from '../../../../state/omeMeet/UseOmeMeetData';
import useOrgUnitAthleteEntryInfoData from '../../../../state/orgUnitAthleteEntryInfo/UseOrgUnitAthleteEntryInfoData';
import useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData from '../../../../state/omeMeetOrgUnitAthleteEntryIndividualRosterEntry/UseOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData';
import useOmeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryData from '../../../../state/omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntry/UseOmeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryData';
import useAthleteEntryContextData from '../../../../state/athleteEntryContextView/UseAthleteEntryContextData';
import useMyAthleteMeetEntriesFiltersData from '../../../../state/myAthleteMeetEntriesFilters/UseMyAthleteMeetEntriesFiltersData';

import useSearchMeetData from '@usas/ui-web/state/searchMeet/UseSearchMeetData';
import useSelectOrgUnitData from '@usas/ui-web/state/selectOrgUnit/UseSelectOrgUnitData';
import useLeftNavModalData from '@usas/ui-web/state/leftNavModal/UseLeftNavModalData';

import Constants from '@usas/ui-web/utils/Constants';

import { formatDate } from '@usas/ui-web/utils/DateFunctions';
import { contactsIsComplete, credentialsIsComplete, rosterEntriesIsComplete } from '../../../../components/leftNavigationOmeAthleteEntry/LeftNavValidation';

const INITIAL_MODAL_STATE = {
  displayPopUp: false,
  modalTitle: 'Delete Credential from Athlete Entry?',
  credentialName: '',
  organization: '',
  memberId: '',
  email: '',
  isInGoodStanding: '',
  isInGoodStandingExpirationDate: '',
  typeName: '',
  orgUnitAthleteEntryOrgUnitNonAthleteEntryId: Constants.DEFAULT_ID
};

const useAthleteUserCredentials = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { omeMeetState } = useOmeMeetData();
  const { orgUnitAthleteEntryInfoState, getOrgUnitAthleteEntryInfo } = useOrgUnitAthleteEntryInfoData();
  const { omeMeetOrgUnitAthleteEntryIndividualRosterEntryState } = useOmeMeetOrgUnitAthleteEntryIndividualRosterEntryData();
  const { omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState, getOrgUnitNonAthleteEntryOrgUnitAthleteEntryCredentials, deleteOrgUnitAthleteEntryOrgUnitNonAthleteEntry } = useOmeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryData();
  const { searchMeetState, setSearchMeetState } = useSearchMeetData();
  const { selectOrgUnitState } = useSelectOrgUnitData();
  const { athleteEntryContextState } = useAthleteEntryContextData();
  const { myAthleteMeetEntriesFiltersState } = useMyAthleteMeetEntriesFiltersData();
  const { leftNavModalState, setLeftNavModalState } = useLeftNavModalData();
  const [modalState, setModalState] = useState(INITIAL_MODAL_STATE);
  const [state, setState] = useState({ tryGet: false, submitButton: '', tryRedirect: false, error: '' });

  const onAddCredential = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }
    if (omeMeetState.route === '') {
      //OME Route
      navigate(EntryNavLinks.ATHLETE_CREDENTIALS_SELECTION);
    }
    else {
      //Meet Route
      navigate(NavLinks.ATHLETE_CREDENTIALS_SELECTION);
    }
  };

  const onDeleteCredentialClicked = (e, credential) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState({
      ...modalState,
      displayPopUp: true,
      credentialName: (credential.firstName || credential.middleName || credential.lastName) ? `${credential.firstName || ''} ${credential.preferredName !== '' && credential.preferredName !== credential.firstName ? '"' + credential.preferredName + '"' : ''} ${credential.middleName || ''} ${credential.lastName || ''}` : '',
      organization: `${credential.orgUnitName || ''} ${credential.parentOrgUnitCode ? '(' + credential.parentOrgUnitCode + ')' : ''}`,
      memberId: credential.memberId || '',
      email: credential.email || '',
      isInGoodStanding: credential.isInGoodStanding === true ? 'Yes' : credential.isInGoodStanding === false ? 'No' : '',
      isInGoodStandingExpirationDate: credential.isInGoodStandingExpirationDate ? formatDate(credential.isInGoodStandingExpirationDate) : '',
      typeName: credential.typeName,
      orgUnitAthleteEntryOrgUnitNonAthleteEntryId: credential.orgUnitAthleteEntryOrgUnitNonAthleteEntryId || Constants.DEFAULT_ID
    });
  };

  const onModalCanceled = (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    setModalState(INITIAL_MODAL_STATE);
  };

  const onDeleteCredential = (e, orgUnitAthleteEntryOrgUnitNonAthleteEntryId) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    deleteOrgUnitAthleteEntryOrgUnitNonAthleteEntry(orgUnitAthleteEntryOrgUnitNonAthleteEntryId);
    onModalCanceled();
    setState({ ...state, tryGet: true, error: '' });
  }

  const onNextButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.CONTINUE, tryRedirect: true, error: '' });
  };

  const onSaveAndExitButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.SAVE_AND_EXIT, tryRedirect: true, error: '' });
  };

  const onBackButtonClicked = (e) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }
    setState({ ...state, submitButton: AthleteUserConstants.BACK, tryRedirect: true, error: '' });
  };

  useEffect(() => {
    setLeftNavModalState({ ...leftNavModalState, formStateSaved: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (location.state && location.state?.tryGet === true) {
      setState({ ...state, tryGet: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (omeMeetOrgUnitAthleteEntryIndividualRosterEntryState.isObjLoaded === true && omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId &&
      omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState.isSaving === false) {
      if ((omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState.isArrayLoaded === false || state.tryGet === true)
        && omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState.isArrayLoading === false) {
        getOrgUnitNonAthleteEntryOrgUnitAthleteEntryCredentials(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId);
        //Make sure to get info when credentials change
        getOrgUnitAthleteEntryInfo(omeMeetOrgUnitAthleteEntryIndividualRosterEntryState?.objData?.orgUnitAthleteEntryId);
        if (state.tryGet === true) {
          setState({ ...state, tryGet: false, error: '' });
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitAthleteEntryIndividualRosterEntryState, omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState, state.tryGet])

  useEffect(() => {
    if (omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState.isSaving === false && state.tryRedirect === true) {
      if (state.submitButton === AthleteUserConstants.CONTINUE) {
        if (credentialsIsComplete(orgUnitAthleteEntryInfoState.objData) === false) {
          setState({
            ...state, tryRedirect: false, submitButton: '', error: `The current credentials exceed the number of allowed credentials according to the meet's deck pass rules. Currently, ${orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber ?? ''} credential${orgUnitAthleteEntryInfoState.objData?.maxCredentialNumber === 1 ? ' is' : 's are'} allowed. ${orgUnitAthleteEntryInfoState.objData?.credentialsCanBeAddedAfterCap === true ? 'Only massage credentials can be added after the cap has been reached.' : 'No credentials can be added after the cap has been reached.'}`
          });
        }
        else if (contactsIsComplete(orgUnitAthleteEntryInfoState.objData) === false || rosterEntriesIsComplete(orgUnitAthleteEntryInfoState.objData) === false) {
          setState({ ...state, tryRedirect: false, error: 'Contacts and Entries must be complete before continuing to Submit.' });
        }
        else {
          if (omeMeetState.route !== '') {
            navigate(NavLinks.ATHLETE_PURCHASES);
          }
          else {
            navigate(EntryNavLinks.ATHLETE_PURCHASES);
          }
        }
      }
      else if (state.submitButton === AthleteUserConstants.SAVE_AND_EXIT) {
        if (omeMeetState.route === '') {
          if (selectOrgUnitState.orgUnitId === '') {
            //My Athlete Meet Entries Route
            navigate(MyAthleteEntryNavLinks.MY_ATHLETE_ENTRY, { state: { myAthleteMeetEntriesFiltersState: myAthleteMeetEntriesFiltersState } });
          }
          else {
            //OME Route
            navigate(MyMeetsNavLinks.MEET_ENTRY_ATHLETE, { state: { selectOrgUnitState: selectOrgUnitState } });
          }
        }
        else {
          //Meet Route
          setSearchMeetState({ ...searchMeetState, showMeetHeader: true });

          navigate(NavLinks.MEET_ENTRY_ATHLETE, { state: { goToMeet: true } });
        }
      }
      else if (state.submitButton === AthleteUserConstants.BACK) {
        if (omeMeetState.route !== '') {
          navigate(NavLinks.ATHLETE_ENTRIES);
        }
        else {
          navigate(EntryNavLinks.ATHLETE_ENTRIES);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState.isSaving, state, orgUnitAthleteEntryInfoState.objData]);

  return {
    state,
    athleteEntryContextState,
    modalState,
    orgUnitAthleteEntryInfoState,
    omeMeetOrgUnitNonAthleteEntryOrgUnitAthleteEntryState,
    onAddCredential,
    onDeleteCredentialClicked,
    onModalCanceled,
    onDeleteCredential,
    onNextButtonClicked,
    onSaveAndExitButtonClicked,
    onBackButtonClicked
  };
}

export default useAthleteUserCredentials;